import XIcon from "@mui/icons-material/Close"
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  Divider,
  IconButton,
  Stack,
  Typography,
} from "@mui/material"
import { DialogProps } from "../types"
import { useSnackbar } from "@/contexts/SnackbarContext"
import { useForm } from "react-hook-form"
import { AdvertisementType } from "@/common/types"
import { z } from "zod"
import { zodResolver } from "@hookform/resolvers/zod"

import ControlledCheckBox from "@/components/controlledInputs/ControlledCheckBox"
import ControlledTextInput from "@/components/controlledInputs/ControlledTextInput"
import { useEffect, useState } from "react"
import { FileWithPath } from "react-dropzone"

import EditAdvertisementFileUpload from "@/components/advertisements/EditAdvertisementFileUpload"
import { s3, warmS3Credentials } from "@/common/globals"
import { PutObjectCommand } from "@aws-sdk/client-s3"
import useUpdateAdvertisementDetails from "@/hooks/api/advertisements/useUpdateAdvertisementDetails"
import useUpdateAdvertisementImages from "@/hooks/api/advertisements/useUpdateAdvertisementImages"

const convertUrlToFile = async (imageUrl: string) => {
  const response = await fetch(imageUrl)
  const blob = await response.blob()
  const filename = imageUrl.split("/").pop() || "image.jpg"
  return new File([blob], filename, { type: blob.type }) as FileWithPath
}

const editAdvertisementSchema = z.object({
  company_name: z.string().optional(),
  action_link: z.string().optional(),
  active: z.boolean().optional(),
  advertisement_images: z
    .object({
      large_image: z.string().nullable().optional(),
      small_image: z.string().nullable().optional(),
      mobile_image: z.string().nullable().optional(),
      description: z.string().optional(),
    })
    .optional(),
})

export type EditAdvertisementFormDataType = z.infer<
  typeof editAdvertisementSchema
>

interface EditAdvertisementDialogProps extends DialogProps {
  advertisement: AdvertisementType
}
export default function EditAdvertisementDialog({
  isOpen,
  closeDialog,
  advertisement,
}: EditAdvertisementDialogProps) {
  const { showSnackbar } = useSnackbar()
  const { mutateAsync: updateAdvertisement } = useUpdateAdvertisementDetails()
  const { mutateAsync: updateAdvertisementImages } =
    useUpdateAdvertisementImages()
  function handleClose() {
    closeDialog()
  }

  const defaultValues = {
    company_name: advertisement.company_name,
    action_link: advertisement.action_link,
    active: Boolean(advertisement.active),
    description: advertisement.description,
    advertisement_images: {
      description: advertisement.description ? advertisement.description : "",
      large_image: advertisement.large_image,
      small_image: advertisement.small_image,
      mobile_image: advertisement.mobile_image,
    },
  }

  const { control, handleSubmit } = useForm({
    resolver: zodResolver(editAdvertisementSchema),
    defaultValues,
  })
  const [largeImage, setLargeImage] = useState<
    FileWithPath | string | undefined
  >(advertisement.large_image)
  const [smallImage, setSmallImage] = useState<
    FileWithPath | string | undefined
  >(advertisement.small_image)
  const [mobileImage, setMobileImage] = useState<
    FileWithPath | string | undefined
  >(advertisement.mobile_image)

  useEffect(() => {
    if (typeof largeImage === "string" && !largeImage) {
      convertUrlToFile(advertisement.small_image).then((file) =>
        setLargeImage(file)
      )
    }
    if (typeof smallImage === "string" && !smallImage) {
      convertUrlToFile(advertisement.small_image).then((file) =>
        setSmallImage(file)
      )
    }
    if (typeof mobileImage === "string" && !mobileImage) {
      convertUrlToFile(advertisement.mobile_image).then((file) =>
        setMobileImage(file)
      )
    }
  }, [
    advertisement.large_image,
    advertisement.small_image,
    advertisement.mobile_image,
    largeImage,
    smallImage,
    mobileImage,
  ])

  const onSubmit = async (data: EditAdvertisementFormDataType) => {
    // Ensure S3 credentials are ready before uploading
    await warmS3Credentials()

    // 1. First update the text details
    const response = await updateAdvertisement({
      id: advertisement.advertisement_id,
      data: {
        company_name: data.company_name,
        action_link: data.action_link,
        active: data.active,
      },
    })

    if (response.error) {
      showSnackbar("Could not update advertisement details", "error")
      return
    }

    // 2. Upload images if changed
    const advertisementId = advertisement.advertisement_id
    const updatedImages: Partial<
      EditAdvertisementFormDataType["advertisement_images"]
    > = {}

    const uploadImage = async (
      image: FileWithPath,
      type: "large_image" | "small_image" | "mobile_image"
    ) => {
      const imageParams = {
        Bucket: import.meta.env.VITE_AWS_ADVERTISEMENT_BUCKET,
        Key: `${advertisementId}/${image.name}`,
        Body: image,
        ContentType: image.type,
      }

      try {
        await s3.send(new PutObjectCommand(imageParams))
        return `${advertisementId}/${image.name}` // Return new S3 path
      } catch (err) {
        showSnackbar(`Could not upload ${type}`, "error")
        console.error(`Error uploading ${type}`, err)
        return advertisement[type] // Keep old URL if upload fails
      }
    }

    if (largeImage instanceof File) {
      updatedImages.large_image = await uploadImage(largeImage, "large_image")
    }
    if (smallImage instanceof File) {
      updatedImages.small_image = await uploadImage(smallImage, "small_image")
    }
    if (mobileImage instanceof File) {
      updatedImages.mobile_image = await uploadImage(
        mobileImage,
        "mobile_image"
      )
    }

    if (data.advertisement_images?.description) {
      updatedImages.description = data.advertisement_images.description
    }

    // 3. Update advertisement with new image paths
    if (Object.keys(updatedImages).length > 0) {
      await updateAdvertisementImages({
        id: advertisementId,
        data: {
          advertisement_images: updatedImages,
        },
      })
    }

    showSnackbar("Advertisement Updated", "success")
    handleClose()
  }

  return (
    <Dialog open={isOpen} onClose={handleClose}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        minWidth={"500px"}
        pl={3}
        pr={1}
        py={1}
        sx={{
          backgroundColor: "primary.main",
          color: "primary.contrastText",
        }}
      >
        <Typography>Edit Advertisement</Typography>
        <Box>
          <IconButton color="inherit" onClick={handleClose}>
            <XIcon />
          </IconButton>
        </Box>
      </Stack>
      <DialogContent
        sx={{
          py: 2,
        }}
      >
        <Box component="form" onSubmit={handleSubmit(onSubmit)}>
          <ControlledTextInput
            variant="outlined"
            control={control}
            name="company_name"
            label="Company Name"
            type="text"
          />
          <ControlledTextInput
            variant="outlined"
            control={control}
            name="action_link"
            label="Action Link"
            type="text"
          />
          <ControlledCheckBox control={control} name="active" label="Active" />
          <Divider sx={{ paddingTop: 2 }} />
          <EditAdvertisementFileUpload
            largeImage={largeImage}
            setLargeImage={setLargeImage}
            smallImage={smallImage}
            setSmallImage={setSmallImage}
            mobileImage={mobileImage}
            setMobileImage={setMobileImage}
          />
          <ControlledTextInput
            variant="outlined"
            control={control}
            name="advertisement_images.description"
            label="Description"
            type="text"
          />
          <Divider sx={{ paddingTop: 2 }} />
          <Stack
            direction="row"
            justifyContent="space-between"
            spacing={2}
            mt={5}
          >
            <Button variant="text" color="inherit" onClick={handleClose}>
              Cancel
            </Button>

            <Button variant="contained" color="primary" type="submit">
              Send
            </Button>
          </Stack>
        </Box>
      </DialogContent>
    </Dialog>
  )
}
